import { useCallback, useEffect, useState } from 'react';
import FormLabel from '~/components/Form/FormLabel';
import extraDaysStyles from '~/styles/bookingFlow/extra-days.module.css';
import PlainNumberField from '~/components/Form/PlainNumberField';
import useTranslation from '~/hooks/useTranslation';
import PlainCheckbox from '~/components/Form/PlainCheckbox';

export type ExtraDaysValue = {
  order_block_id: number;
  name: string;
  quantity: number;
  min: number;
  max: number;
};

type Props = {
  value: ExtraDaysValue[];
  onChange: (value: ExtraDaysValue[]) => void;
};

export default function ExtraDays({
  value,
  onChange,
}: Props) {
  const translate = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const onUpdateExtraDaysQuantity = useCallback((orderBlockId: number, quantity: number) => {
    const newExtraDays = [...value].map((extraDaysItem) => {
      if (extraDaysItem.order_block_id === orderBlockId) {
        return { ...extraDaysItem, quantity };
      }

      return extraDaysItem;
    });

    onChange(newExtraDays);
  }, [value, onChange]);

  const onToggleExtraDays = useCallback(() => {
    // If the user closed the extra nights/days panel
    if (isOpen) {
      // Change extra days quantity to 0 on all items
      const newExtraDays = [...value].map((extraDaysItem) => {
        return { ...extraDaysItem, quantity: 0 };
      });

      onChange(newExtraDays);
    }

    setIsOpen(!isOpen);
  }, [isOpen, onChange, value]);

  useEffect(() => {
    const hasPositiveQuantity = value.some((extraDaysItem) => extraDaysItem.quantity > 0);

    if (hasPositiveQuantity) {
      setIsOpen(true);
    }
  }, [value]);

  return (
    <div>
      <PlainCheckbox
        label={translate('bookingFlow.information.add_extra_days')}
        checked={isOpen}
        onChange={onToggleExtraDays}
      />

      {isOpen && (
        <div className={extraDaysStyles.extraDays}>
          <FormLabel>
            {translate('bookingFlow.information.extra_days')}
          </FormLabel>

          <div className={extraDaysStyles.extraDaysRows}>
            {value.map((extraDaysItem) => (
              <div key={extraDaysItem.order_block_id} className={extraDaysStyles.extraDaysRow}>
                <div className={extraDaysStyles.extraDaysQuantity}>
                  <PlainNumberField
                    label={`${extraDaysItem.name} quantity`}
                    value={extraDaysItem.quantity}
                    min={extraDaysItem.min}
                    max={extraDaysItem.max}
                    onChange={
                      (quantity) => onUpdateExtraDaysQuantity(
                        extraDaysItem.order_block_id,
                        quantity,
                      )
                    }
                    visuallyHiddenLabel
                  />
                </div>

                <div className={extraDaysStyles.extraDaysName}>
                  {extraDaysItem.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
